import api from './api';
import type {
  CrearFacturaCompraRequest,
  CrearFacturaCompraResponse,
  FiltrosFacturaCompra,
  FacturaCompraListResponse,
  FacturaCompraResponse,
  ValidarDuplicadoRequest,
  ValidarDuplicadoResponse,
  FacturaOCRResponse,
} from '../types/facturas';

export type EditarFacturaCompraRequest = CrearFacturaCompraRequest;

/**
 * Servicio para gestión de facturas de compra
 */
class FacturasCompraService {
  private readonly baseURL = '/facturas-compra';

  /**
   * Listar facturas con filtros opcionales
   */
  async listar(
    filtros?: FiltrosFacturaCompra
  ): Promise<FacturaCompraListResponse> {
    const params = new URLSearchParams();

    if (filtros?.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
    if (filtros?.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
    if (filtros?.proveedor_id)
      params.append('proveedor_id', filtros.proveedor_id.toString());
    if (filtros?.estado) params.append('estado', filtros.estado);
    if (filtros?.numero_factura)
      params.append('numero_factura', filtros.numero_factura);
    if (filtros?.limit) params.append('limit', filtros.limit.toString());
    if (filtros?.offset) params.append('offset', filtros.offset.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.baseURL}?${queryString}` : this.baseURL;

    const response = await api.get<FacturaCompraListResponse>(url);
    return response.data;
  }

  /**
   * Obtener detalle de una factura
   */
  async obtenerPorId(id: number): Promise<FacturaCompraResponse> {
    const response = await api.get<FacturaCompraResponse>(
      `${this.baseURL}/${id}`
    );
    return response.data;
  }

  /**
   * Validar si existe factura duplicada
   */
  async validarDuplicado(
    data: ValidarDuplicadoRequest
  ): Promise<ValidarDuplicadoResponse> {
    const response = await api.post<ValidarDuplicadoResponse>(
      `${this.baseURL}/validar-duplicado`,
      data
    );
    return response.data;
  }

  /**
   * Crear nueva factura de compra
   */
  async crear(
    data: CrearFacturaCompraRequest
  ): Promise<CrearFacturaCompraResponse> {
    const response = await api.post<CrearFacturaCompraResponse>(
      this.baseURL,
      data
    );
    return response.data;
  }

  /**
   * Editar una factura existente (solo en estado borrador)
   */
  async editar(
    id: number,
    data: EditarFacturaCompraRequest
  ): Promise<CrearFacturaCompraResponse> {
    const response = await api.put<CrearFacturaCompraResponse>(
      `${this.baseURL}/${id}`,
      data
    );
    return response.data;
  }

  /**
   * Anular una factura
   */
  async anular(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(
      `${this.baseURL}/${id}`
    );
    return response.data;
  }

  /**
   * Procesar imagen con OCR
   */
  async procesarOCR(archivo: File): Promise<FacturaOCRResponse> {
    const formData = new FormData();
    formData.append('imagen', archivo);

    const response = await api.post<FacturaOCRResponse>(
      `${this.baseURL}/ocr`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 segundos para OCR
      }
    );

    return response.data;
  }

  /**
   * Obtener estadísticas de facturas
   */
  async obtenerEstadisticas(
    fechaDesde?: string,
    fechaHasta?: string
  ): Promise<{
    total_facturas: number;
    total_monto: number;
    facturas_por_proveedor: {
      proveedor: string;
      total: number;
      monto: number;
    }[];
    facturas_por_estado: { estado: string; cantidad: number }[];
  }> {
    const params = new URLSearchParams();
    if (fechaDesde) params.append('fecha_desde', fechaDesde);
    if (fechaHasta) params.append('fecha_hasta', fechaHasta);

    const queryString = params.toString();
    const url = queryString
      ? `${this.baseURL}/estadisticas?${queryString}`
      : `${this.baseURL}/estadisticas`;

    const response = await api.get(url);
    return response.data;
  }
}

// Exportar instancia singleton
export const facturasCompraService = new FacturasCompraService();

// Exportar también como default
export default facturasCompraService;
